import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowLeft, Check, FileDown, Plus, RotateCcw, Send, Trash2, X } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { NOME_STATUS_ORC, classeStatusOrc, dataBr, real, type OrcItem, type Orcamento } from './comum';

interface Opcao { id: string; tipo: 'peca' | 'servico'; nome: string; sku: string | null; preco: string | null; unidade: string }
const num = (v: string | number) => (typeof v === 'number' ? v : Number(String(v).replace(/\./g, '').replace(',', '.')));
const fmt = (v: string | number) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function DetalheOrcamento() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const podeEditar = pode(usuario, 'orcamentos.editar');
  const podeDecidir = pode(usuario, 'orcamentos.decidir');
  const [o, setO] = useState<Orcamento | null>(null);
  const [itens, setItens] = useState<OrcItem[]>([]);
  const [campos, setCampos] = useState({ titulo: '', condicoes: '', observacoes: '', validade: '' });
  const [sujo, setSujo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [recusar, setRecusar] = useState(false);
  const [motivo, setMotivo] = useState('');

  const aplicar = (x: Orcamento) => {
    setO(x); setItens((x.itens ?? []).map((i) => ({ ...i, quantidade: fmtQtd(i.quantidade), preco_unitario: fmt(i.preco_unitario), desconto: fmt(i.desconto) })));
    setCampos({ titulo: x.titulo ?? '', condicoes: x.condicoes ?? '', observacoes: x.observacoes ?? '', validade: x.validade ?? '' }); setSujo(false);
  };
  const carregar = useCallback(() => { api<Orcamento>(`/orcamentos/${id}`).then(aplicar).catch((e) => setErro(e.message)); }, [id]);
  useEffect(() => { carregar(); }, [carregar]);
  if (!o) return erro ? <p className="alerta">{erro}</p> : <p className="nota">Carregando…</p>;

  const editavel = podeEditar && o.status === 'rascunho';
  const totalTela = itens.reduce((a, i) => a + Math.round((num(i.quantidade) * num(i.preco_unitario) - num(i.desconto)) * 100) / 100, 0);
  const muda = (k: number, campo: keyof OrcItem, v: string) => { setItens((l) => l.map((x, j) => (j === k ? { ...x, [campo]: v } : x))); setSujo(true); };

  async function salvar() {
    setErro(null); setOk(null);
    try {
      aplicar(await api<Orcamento>(`/orcamentos/${o!.id}`, { metodo: 'PATCH', corpo: {
        versao: o!.versao, titulo: campos.titulo.trim() || null, condicoes: campos.condicoes.trim() || null, observacoes: campos.observacoes.trim() || null,
        validade: campos.validade || null,
        itens: itens.map((i) => ({ produto_id: i.produto_id, tipo: i.tipo, descricao: i.descricao.trim(), quantidade: num(i.quantidade), preco_unitario: num(i.preco_unitario), desconto: num(i.desconto) || 0 })),
      } }));
      setOk('Orçamento salvo.');
    } catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível salvar.'); }
  }
  async function acao(rota: string, corpo: Record<string, unknown> = {}) {
    setErro(null); setOk(null);
    try { aplicar(await api<Orcamento>(`/orcamentos/${o!.id}/${rota}`, { metodo: 'POST', corpo: { versao: o!.versao, ...corpo } })); setRecusar(false); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível concluir.'); }
  }

  return (
    <>
      <Link to="/orcamentos" className="voltar"><ArrowLeft size={16} aria-hidden /> Orçamentos</Link>
      <div className="cabecalho-pagina">
        <div>
          <span className="kicker">{o.os_numero ? `Levantado na OS ${o.os_numero}` : 'Orçamento'}</span>
          <h1 className="titulo mono">{o.numero}</h1>
          <div className="meta">
            <span className={`situacao ${classeStatusOrc(o.status)}`}>{NOME_STATUS_ORC[o.status]}</span>
            <Link to={`/clientes/${o.cliente_id}`}>{o.cliente}</Link>{o.local && <span>· {o.local}</span>}
            {o.os_id && <Link to={`/os/${o.os_id}`}>OS {o.os_numero}</Link>}
            {o.criado_por && <span>por {o.criado_por}</span>}
          </div>
        </div>
        <div className="acoes-cabecalho">
          <a className="btn-fantasma" href={`/api/orcamentos/${o.id}/pdf`} target="_blank" rel="noreferrer"><FileDown size={16} aria-hidden /> PDF</a>
          {editavel && <button className="btn-fantasma" onClick={salvar} disabled={!sujo}>Salvar</button>}
          {editavel && <button className="btn" onClick={async () => { if (sujo) await salvar(); acao('enviar'); }}><Send size={16} aria-hidden /> Marcar como enviado</button>}
          {podeEditar && o.status === 'enviado' && <button className="btn-fantasma" onClick={() => acao('reabrir')}><RotateCcw size={16} aria-hidden /> Voltar a rascunho</button>}
          {podeDecidir && o.status === 'enviado' && <button className="btn-fantasma" onClick={() => acao('aprovar')}><Check size={16} aria-hidden /> Cliente aprovou</button>}
          {podeDecidir && o.status === 'enviado' && <button className="btn-fantasma perigo" onClick={() => setRecusar(true)}><X size={16} aria-hidden /> Recusou</button>}
          {podeEditar && ['rascunho', 'enviado'].includes(o.status) && <button className="btn-fantasma perigo" onClick={() => acao('cancelar')}>Cancelar</button>}
        </div>
      </div>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {ok && <p className="alerta alerta-ok">{ok}</p>}
      {o.status === 'rascunho' && o.os_numero && <p className="alerta alerta-aviso">Montado pelo técnico em campo. Confira itens e preços antes de enviar ao cliente.</p>}
      {o.status === 'recusado' && <p className="alerta">Recusado: {o.motivo_recusa}</p>}

      <section className="cartao tabela-cartao rolagem-x">
        <table className="tabela tabela-orc">
          <thead><tr><th>Descrição</th><th className="num">Qtde</th><th className="num">Unitário (R$)</th><th className="num">Desconto (R$)</th><th className="num">Total</th>{editavel && <th></th>}</tr></thead>
          <tbody>
            {itens.length === 0 && <tr><td colSpan={6}><span className="nota">Nenhum item.</span></td></tr>}
            {itens.map((i, k) => {
              const sub = num(i.quantidade) * num(i.preco_unitario) - num(i.desconto);
              return (
                <tr key={k}>
                  <td>{editavel ? <input value={i.descricao} onChange={(e) => muda(k, 'descricao', e.target.value)} maxLength={300} /> : i.descricao}
                    <small>{i.tipo === 'servico' ? 'Serviço' : 'Peça'}{i.sku ? ` · ${i.sku}` : ''}</small></td>
                  <td className="num">{editavel ? <input inputMode="decimal" value={String(i.quantidade)} onChange={(e) => muda(k, 'quantidade', e.target.value)} style={{ width: 70, textAlign: 'right' }} /> : fmtQtd(i.quantidade)}</td>
                  <td className="num">{editavel ? <input inputMode="decimal" value={String(i.preco_unitario)} onChange={(e) => muda(k, 'preco_unitario', e.target.value)} style={{ width: 110, textAlign: 'right' }} /> : fmt(i.preco_unitario)}</td>
                  <td className="num">{editavel ? <input inputMode="decimal" value={String(i.desconto)} onChange={(e) => muda(k, 'desconto', e.target.value)} style={{ width: 100, textAlign: 'right' }} /> : (num(i.desconto) ? fmt(i.desconto) : '—')}</td>
                  <td className="num"><strong>{Number.isFinite(sub) ? real(sub) : '—'}</strong></td>
                  {editavel && <td><button className="btn-icone" aria-label="Remover item" onClick={() => { setItens((l) => l.filter((_, j) => j !== k)); setSujo(true); }}><Trash2 size={16} /></button></td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr><td colSpan={4} className="num"><strong>Total</strong></td><td className="num"><strong>{real(editavel ? totalTela : o.total)}</strong></td>{editavel && <td></td>}</tr></tfoot>
        </table>
        {editavel && <AdicionarItem aoAdicionar={(it) => { setItens((l) => [...l, it]); setSujo(true); }} />}
      </section>

      <div className="grade-ficha secao">
        <section className="cartao ficha form">
          <h2>Para o cliente</h2>
          <label>Objeto<input value={campos.titulo} disabled={!editavel} onChange={(e) => { setCampos({ ...campos, titulo: e.target.value }); setSujo(true); }} maxLength={200} /></label>
          <label>Condições (pagamento, prazo de execução)<textarea rows={3} value={campos.condicoes} disabled={!editavel} onChange={(e) => { setCampos({ ...campos, condicoes: e.target.value }); setSujo(true); }} maxLength={2000} placeholder="Ex.: 50% na aprovação e 50% na entrega. Execução em até 7 dias úteis." /></label>
          <label>Observações<textarea rows={3} value={campos.observacoes} disabled={!editavel} onChange={(e) => { setCampos({ ...campos, observacoes: e.target.value }); setSujo(true); }} maxLength={4000} /></label>
          <label>Validade<input type="date" value={campos.validade} disabled={!editavel} onChange={(e) => { setCampos({ ...campos, validade: e.target.value }); setSujo(true); }} /></label>
        </section>
        <section className="cartao ficha">
          <h2>Histórico</h2>
          <dl>
            <div><dt>Criado</dt><dd>{new Date(o.criado_em).toLocaleString('pt-BR')}{o.criado_por ? ` · ${o.criado_por}` : ''}</dd></div>
            <div><dt>Enviado</dt><dd>{o.enviado_em ? `${new Date(o.enviado_em).toLocaleString('pt-BR')}${o.enviado_por ? ` · ${o.enviado_por}` : ''}` : '—'}</dd></div>
            <div><dt>Decisão do cliente</dt><dd>{o.decidido_em ? `${new Date(o.decidido_em).toLocaleString('pt-BR')}${o.decidido_por ? ` · registrado por ${o.decidido_por}` : ''}` : '—'}</dd></div>
            <div><dt>Válido até</dt><dd>{dataBr(o.validade)}</dd></div>
          </dl>
          <p className="nota">"Marcar como enviado" registra que o orçamento foi ao cliente (o PDF você manda pelo WhatsApp ou e-mail). Enviado não muda mais; para corrigir, volte a rascunho.</p>
        </section>
      </div>

      <Modal titulo="Cliente recusou" aberto={recusar} aoFechar={() => setRecusar(false)}>
        <div className="form">
          <label>Motivo<textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder='Ex.: "Achou caro", "Fechou com outra empresa", "Vai deixar para o ano que vem"' /></label>
          <div className="acoes-form"><button className="btn-fantasma" onClick={() => setRecusar(false)}>Voltar</button>
            <button className="btn" disabled={motivo.trim().length < 3} onClick={() => acao('recusar', { motivo: motivo.trim() })}>Registrar recusa</button></div>
        </div>
      </Modal>
    </>
  );
}

function fmtQtd(v: string | number) { return Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 }); }

function AdicionarItem({ aoAdicionar }: { aoAdicionar: (i: OrcItem) => void }) {
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState<Opcao[]>([]);
  useEffect(() => {
    if (busca.trim().length < 2) return setOpcoes([]);
    const t = setTimeout(async () => {
      const [p, s] = await Promise.all([
        api<{ id: string; nome: string; sku: string | null; preco: string | null; unidade: string }[]>(`/produtos?busca=${encodeURIComponent(busca)}&limite=8`).catch(() => []),
        api<{ id: string; nome: string; sku: string | null; preco: string | null; unidade: string }[]>(`/servicos?busca=${encodeURIComponent(busca)}`).catch(() => []),
      ]);
      setOpcoes([...s.slice(0, 6).map((x) => ({ ...x, tipo: 'servico' as const })), ...p.map((x) => ({ ...x, tipo: 'peca' as const }))]);
    }, 250);
    return () => clearTimeout(t);
  }, [busca]);
  const incluir = (o: Opcao | null) => {
    aoAdicionar(o ? { produto_id: o.id, tipo: o.tipo, descricao: o.nome, quantidade: '1', preco_unitario: fmt(o.preco ?? 0), desconto: '0,00', sku: o.sku }
      : { produto_id: null, tipo: 'peca', descricao: busca.trim(), quantidade: '1', preco_unitario: '0,00', desconto: '0,00' });
    setBusca(''); setOpcoes([]);
  };
  return (
    <div className="adicionar-item">
      <div className="autocompletar">
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="+ Adicionar peça ou serviço (busque no catálogo ou digite um item avulso)" />
        {busca.trim().length >= 2 && (
          <ul>
            {opcoes.map((o) => <li key={`${o.tipo}${o.id}`}><button type="button" onClick={() => incluir(o)}><strong>{o.nome}</strong><small>{o.tipo === 'servico' ? 'Serviço' : 'Peça'}{o.sku ? ` · ${o.sku}` : ''} · {o.preco ? real(o.preco) : 'sem preço'}</small></button></li>)}
            <li><button type="button" onClick={() => incluir(null)}><Plus size={14} aria-hidden /> Item avulso: "{busca.trim()}"</button></li>
          </ul>
        )}
      </div>
    </div>
  );
}
